/**
 * UmiJS Max runtime configuration - the application entry point.
 *
 * Nothing here is imported by app code; the framework picks the exports up by
 * name:
 * - `getInitialState` resolves the signed-in user before the first render and
 *   doubles as the auth gate.
 * - `layout` configures ProLayout (mostly to disable it, see below).
 * - `request` installs the HTTP interceptors.
 *
 * @see https://umijs.org/docs/max/runtime-config
 */
import { getMe } from '@/services/rest/api';
import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
// import CustomMenu from './components/CustomMenu';
import {
  isUrlMatchingPrefix,
  SHARE_PATH,
  LOGIN_PATH,
  SCREEN_CLASS,
  screenWidthClassifier,
} from '@/utils';

const isDev = process.env.NODE_ENV === 'development';

/**
 * Builds the global state exposed as `useModel('@@initialState')`.
 *
 * UmiJS awaits this before the first render, which is what turns it into the
 * app's auth gate: no protected screen can flash before `currentUser` is known.
 *
 * @see https://umijs.org/docs/max/initial-state
 */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: DATA.UserDefine;
  loading?: boolean;
  screenClass?: SCREEN_CLASS;
  fetchUserInfo?: () => Promise<DATA.UserDefine | undefined>;
}> {
  const screenClass = screenWidthClassifier(window.innerWidth);

  // Strict variant: a failed session lookup kicks the visitor out to the login
  // page. Also handed down in the initial state so screens can refresh the user.
  const fetchUserInfo = async () => {
    try {
      const msg = await getMe();
      return msg.data.user;
    } catch (error) {
      history.push(LOGIN_PATH);
    }
    return undefined;
  };
  // Tolerant variant: resolves to `undefined` instead of redirecting, so public
  // pages still render for logged-out visitors while picking up a session when
  // one happens to exist.
  const otherFetchUserInfo = async () => {
    try {
      const msg = await getMe();
      return msg.data.user;
    } catch (error) {
      return undefined;
    }
  };
  const { location } = history;

  // Allowlisted routes (login / register / reset / verify / initiate / share)
  // must render without a session.

  if (isUrlMatchingPrefix(location.pathname) === true) {
    // Share pages are the one allowlisted family that still benefits from a
    // session (owner-only affordances), hence the non-redirecting lookup.
    if (location.pathname.startsWith(SHARE_PATH)) {
      const currentUser = await otherFetchUserInfo();
      return {
        fetchUserInfo,
        screenClass,
        currentUser,
        settings: defaultSettings as Partial<LayoutSettings>,
      };
    }
    return {
      fetchUserInfo,
      screenClass,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  // Everything else is behind the gate: resolve the user or get redirected.
  const currentUser = await fetchUserInfo();
  return {
    fetchUserInfo,
    screenClass,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

/**
 * ProLayout runtime config.
 *
 * Note this config is inert: every top-level route in `config/routes.ts` sets
 * `layout: false`, so ProLayout never mounts. The product draws its own chrome
 * in `@/layouts/BasicLayout`.
 *
 * What is still load-bearing here is `onPageChange` - the navigation-time half
 * of the auth gate, which umi calls regardless of ProLayout - and the dev-only
 * settings drawer in `childrenRender`.
 *
 * @see https://procomponents.ant.design/components/layout
 */
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    headerRender: undefined,
    menuRender: undefined,
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => undefined,
    // `getInitialState` only runs once, so the gate has to be re-asserted on
    // every client-side navigation.
    onPageChange: () => {
      const { location } = history;
      if (initialState?.currentUser === undefined) {
        if (isUrlMatchingPrefix(location.pathname) === false) {
          history.push(LOGIN_PATH);
        }
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI docs</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // Custom 403 screen, if one is ever needed:
    // unAccessible: <div>unAccessible</div>,
    // Wraps every routed page; used here only to bolt the dev settings drawer on.
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * Request plugin configuration.
 *
 * Delegates entirely to `@/requestErrorConfig`, which attaches the bearer token,
 * unwraps the `{ success, errorCode, errorMessage, data }` envelope and replays
 * requests once after refreshing an expired access token.
 *
 * @doc https://umijs.org/docs/max/request
 */
export const request = {
  ...errorConfig,
};
